<script setup>
import { ref, computed, getCurrentInstance, onMounted } from 'vue';
import draggable from 'vuedraggable';
import DragHandle from '../../common/DragHandle';

const { proxy } = getCurrentInstance();
const store = proxy.$store;
const router = proxy.$router;

// --- State ---

const isDragging = ref(false);
const ajaxInProgress = ref(false);
const dragImg = ref(null);

// Create the blank drag image (transparent pixel)
const initDragImage = () => {
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    dragImg.value = img;
};
initDragImage();

// --- Computed ---

const selectedId = computed(() => parseInt(proxy.$route.params.fieldsetId));

const items = computed({
    get: () => store.state.componentFields.fieldsets,
    set: (value) => store.commit('componentFields/fieldsets', value)
});

const dragOptions = computed(() => ({
    animation: 200,
    ghostClass: 'ghost',
    setData: (dataTransfer) => {
        // Use the blank image to hide the default browser ghosting
        if (dragImg.value) {
            dataTransfer.setDragImage(dragImg.value, 0, 0);
        }
    }
}));

// --- Methods ---

const editItem = (id) => {
    router.push({
        name: 'componentFields',
        params: { fieldsetId: id, fieldId: null }
    });
};

const onDragEnd = (obj) => {
    isDragging.value = false;
    const item = items.value[obj.newIndex];

    ajaxInProgress.value = true;
    store.dispatch('itemData/update', {
            tableName: 'fieldset',
            id: item.id,
            data: { displayOrder: obj.newIndex + 1 }
        })
        .then(() => {
            return store.dispatch('componentFields/reloadFieldsets');
        })
        .finally(() => {
            ajaxInProgress.value = false;
        });
};

const setFormData = (obj) => {
    store.state.componentFields.fieldsetFormData = JSON.parse(JSON.stringify(obj));
};
</script>

<template>
    <div>
        <div :class="{ 'content-type--items-is-dragging': isDragging }">
            <draggable
                v-if="items.length"
                v-model="items"
                direction="vertical"
                draggable="[data-draggable-item]"
                v-bind="dragOptions"
                @end="onDragEnd"
                @start="isDragging = true"
            >
                <transition-group class="list-group overflow-hidden" type="transition">
                    <a v-for="item in items"
                       :key="item.id"
                       ref="contentTypeItem"
                       :class="{ 'active': item.id === selectedId, 'disabled': ajaxInProgress }"
                       class="list-group-item ps-1 d-flex align-items-center"
                       data-draggable-item
                       draggable="true"
                       href
                       @click.prevent="editItem(item.id)"
                    >
                        <DragHandle class="content-type--item-drag-handle"/>

                        <span class="flex-fill">{{ item.legend }}</span>

                        <i data-bs-toggle="offcanvas"
                           data-bs-target="#contentTypeFieldsetOffcanvas"
                           class="bi-pencil-square"
                           role="button"
                           aria-controls="contentTypeFieldsetOffcanvas"
                           @click.prevent.stop="setFormData(item)"
                        />
                    </a>
                </transition-group>
            </draggable>
        </div>
    </div>
</template>

<style scoped>
.ghost {
    background: #666 !important;
    border-color: #666 !important;
    color: #fff !important;
}

.content-type--item-drag-handle {
    opacity: 0;
    color: #999;
}

.list-group-item:hover .content-type--item-drag-handle {
    opacity: 1;
}

.list-group-item i {
    opacity: 0;
}

.list-group-item:hover i {
    opacity: 1;
}

.content-type--items-is-dragging .content-type--item-drag-handle {
    opacity: 0 !important;
}
</style>